import { useCallback, useMemo } from 'react'
import { usePageSettings } from '@providers/page-settings'

import { useRegions } from '../../regions/provider'
import { replaceDynamicVars } from '../../regions/utils/replacer'
import { AvailableComponentTypes, NodeParam, PageVariable } from '../types'

type Getter = <T>(componentType: AvailableComponentTypes, defaultValue?: T) => string | T

const getParam = <T>(
	params: NodeParam[],
	componentType: AvailableComponentTypes,
	replacer: (string) => string,
	defaultValue?: T
) => {
	const rawValue = params.find((n) => n.componentType === componentType)?.value || defaultValue
	if (typeof rawValue === 'string') {
		return replacer(rawValue)
	}
	return rawValue
}

export const useVarsReplacer = (): ((raw: string) => string) => {
	const region = useRegions()
	const pageSettings = usePageSettings()
	const extraVars: { [key: string]: string } = useMemo(() => {
		const adminVariables = (pageSettings?.admin?.page?.variables || []).reduce(
			(acc, cur: PageVariable) => {
				acc[`%${cur.key}%`] = cur.value
				return acc
			},
			{}
		)

		return adminVariables
	}, [pageSettings])

	const replacer = useCallback(
		(raw: string) => {
			return replaceDynamicVars(region.currentCity, { value: raw }, extraVars).value
		},
		[region.currentCity, extraVars]
	)

	return replacer
}

export const useNodeParams = (params: NodeParam[]): { get: Getter } => {
	const replacer = useVarsReplacer()

	const getter = useCallback(
		(componentType, defaultValue) => {
			return getParam(params, componentType, replacer, defaultValue)
		},
		[params]
	)

	return {
		get: getter,
	}
}

export const useNodeParamsGetterCreator = (): { createGetter(params: NodeParam[]): Getter } => {
	const replacer = useVarsReplacer()

	const createGetter = useCallback(
		(params: NodeParam[]): Getter => {
			return (componentType, defaultValue) =>
				getParam(params, componentType, replacer, defaultValue)!
		},
		[replacer]
	)

	return {
		createGetter,
	}
}
